import type { CButtonVariants } from '@/components/ui/CButton.vue'
import type { CButtonCombinedType } from '@/components/ui/CButtonCombined.vue'

import { VisibilityType } from '#shared/types/visibility'
import merge from 'lodash-es/merge'

const makeVisibilityButtonItems = (
    overrides: Partial<
        Record<
            VisibilityType,
            Partial<{
                button?: { text?: string }
                dropdown?: { text?: string; description?: string }
            }>
        >
    >
): CButtonCombinedType[] => [
    merge(
        {
            id: VisibilityType.published,
            icon: 'view',
            button: {
                variant: 'success-150' as keyof typeof CButtonVariants,
                text: 'Príležitosť vidia všetci'
            },
            dropdown: {
                text: 'Vidia všetci',
                description: 'Môže si pozrieť každý. Zobrazí sa v zoznamoch a vo výsledkoch vyhľadávania.'
            }
        },
        overrides[VisibilityType.published]
    ),
    merge(
        {
            id: VisibilityType.unlisted,
            icon: 'link',
            button: {
                variant: 'gray-750' as keyof typeof CButtonVariants,
                text: 'Prístupné iba cez link'
            },
            dropdown: {
                text: 'Prístupné iba cez link',
                description:
                    'Zobraziť môžu iba tí, ktorí majú link. Nezobrazí sa v zoznamoch ani vo výsledkoch vyhľadávania.'
            }
        },
        overrides[VisibilityType.unlisted]
    ),
    merge(
        {
            id: VisibilityType.hidden,
            icon: 'lock',
            button: {
                variant: 'gray-750' as keyof typeof CButtonVariants,
                text: 'Príležitosť je ukončená'
            },
            dropdown: {
                text: 'Ukončené',
                description:
                    'Nezobrazí sa v zoznamoch ani výsledkoch vyhľadávania. V aktivitách bude medzi Ukončenými. Tento stav viete kedykoľvek vrátiť späť. Záujemcovia, ktorí sa už prihlásili, nebudú o zmene notifikovaní.'
            }
        },
        overrides[VisibilityType.hidden]
    )
]
export const jobVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Ponuku vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        button: { text: 'Prístupná iba cez link' }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Príležitosť je ukončená' }
    }
})
export const projectVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Projekt vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        button: { text: 'Prístupný iba cez link' }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Projekt je ukončený' }
    }
})
export const workgroupSkillVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Skupinu vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        button: { text: 'Prístupná iba cez link' }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Skupina je skrytá' },
        dropdown: { text: 'Skrytá' }
    }
})
export const eventSkillVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Udalosť vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        button: { text: 'Prístupná iba cez link' }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Udalosť je ukončená' },
        dropdown: { text: 'Skrytá' }
    }
})
export const courseSkillVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Kurz vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        button: { text: 'Prístupný iba cez link' }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Kurz je ukončený' }
    }
})
export const studyOpportunitySkillVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Štipendium vidia všetci' }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Štipendium je ukončené' }
    }
})

export const mentorProfileVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Profil vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        dropdown: {
            description:
                'Tvoj mentorský profil môžu zobraziť iba tí, ktorí majú link. Nezobrazí sa v zoznamoch ani vo výsledkoch vyhľadávania.'
        }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Profil je súkromný' },
        dropdown: {
            text: 'Súkromné',
            description:
                'Tvoj mentorský profil sa nezobrazí v zoznamoch ani výsledkoch vyhľadávania. Tento stav vieš kedykoľvek vrátiť späť. Záujemcovia, ktorí ťa už oslovili, nebudú o zmene notifikovaní.'
        }
    }
})

export const volunteerProfileVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Profil vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        dropdown: {
            description:
                'Tvoj dobrovoľnícky profil môžu zobraziť iba tí, ktorí majú link. Nezobrazí sa v zoznamoch ani vo výsledkoch vyhľadávania.'
        }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Profil je súkromný' },
        dropdown: {
            text: 'Súkromné',
            description:
                'Tvoj dobrovoľnícky profil sa nezobrazí v zoznamoch ani výsledkoch vyhľadávania. Tento stav vieš kedykoľvek vrátiť späť. Záujemcovia, ktorí ťa už oslovili, nebudú o zmene notifikovaní.'
        }
    }
})

export const menteeProfileVisibilityButtonItems = makeVisibilityButtonItems({
    [VisibilityType.published]: {
        button: { text: 'Profil vidia všetci' }
    },
    [VisibilityType.unlisted]: {
        dropdown: {
            description:
                'Tvoj mentee profil môžu zobraziť iba tí, ktorí majú link. Nezobrazí sa v zoznamoch ani vo výsledkoch vyhľadávania.'
        }
    },
    [VisibilityType.hidden]: {
        button: { text: 'Profil je súkromný' },
        dropdown: {
            text: 'Súkromné',
            description:
                'Tvoj mentee profil sa nezobrazí v zoznamoch ani výsledkoch vyhľadávania. Tento stav vieš kedykoľvek vrátiť späť.'
        }
    }
})
